import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useConfirmation } from '@/contexts/confirmation'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import {
    INVOICE_STATUS_OPTIONS,
    INVOICE_TYPES,
} from '@/modules/Invoice/constants'
import { approvedInvoice, getInvoices } from '@/modules/Invoice/services'
import { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
    getInvoiceListColumns,
    getInvoiceListFilterItems,
} from '../utils/invoice-list-config'
import { SIGNED_DOCUMENT_STATUS_FIELDS } from '../constants/signed-documents'
import type { GetInvoicesRow } from '../types'

/**
 * Shared filters interface for Invoice tabs
 * Includes invoice_status to preserve filters when switching tabs
 */
interface InvoiceFilters {
    invoice_status: string[]
    contract_role: string[]
    contract_direction: string[]
    product_group_name: string[]
    account_manager_codes: string[]
    invoice_apply_startdate: string[]
    invoice_apply_startdate_to: string[]
    invoice_apply_enddate: string[]
}

/**
 * Business logic hook for Invoice Waiting Confirm List
 * Filters invoices waiting for confirmation with selection capability
 */
export const useInvoiceWaitingConfirm = () => {
    const { t } = useTranslation(['invoice'])
    const [selectedInvoices, setSelectedInvoices] = useState<string[]>([])
    const { setConfirmation } = useConfirmation()

    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<InvoiceFilters>({
        defaults: {
            sort_by: 'invoice_id',
            sort_order: 'desc',
            page_size: 20,
        },
        filterDefaults: {
            invoice_status: [],
            contract_role: [],
            contract_direction: [],
            product_group_name: [],
            account_manager_codes: [],
            invoice_apply_startdate: [],
            invoice_apply_startdate_to: [],
            invoice_apply_enddate: [],
        },
    })

    // Form for filter controls
    const { control } = useForm<InvoiceFilters>({
        defaultValues: filters,
    })

    // Build query params merging URL filters with tab-specific filters
    const apiParams = useMemo(() => {
        // Parse URL filters
        let urlFilters: Record<string, unknown> = {}
        if (queryParams.filters) {
            try {
                urlFilters = JSON.parse(atob(queryParams.filters))
            } catch {
                urlFilters = {}
            }
        }

        // Merge with tab-specific filters (waiting-confirm)
        const mergedFilters = {
            ...urlFilters,
            // Tab này không có chứng từ ký số: bỏ filter giữ lại từ tab khác
            payment_request_status: [],
            reconciliation_statement_status: [],
            // Chưa xác nhận thì chưa phát hành hoá đơn điện tử
            e_invoice_status: [],
            revenue_confirmed: [false],
            // Gồm cả hoá đơn đối soát đã duyệt để ký số nhưng CS chưa xác nhận với khách
            invoice_status: [
                INVOICE_STATUS_OPTIONS.WaitingConfirm,
                INVOICE_STATUS_OPTIONS.Approved,
            ],
            invoice_type: [INVOICE_TYPES.Default],
        }

        return {
            ...queryParams,
            filters: btoa(JSON.stringify(mergedFilters)),
        }
    }, [queryParams])

    const { data, renderState, isFetching } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.INVOICES, 'waiting-confirm', apiParams],
        queryFn: () => getInvoices(apiParams),
        placeholderData: true,
    })

    const approveMutation = useMutationWithHandler(
        {
            mutationFn: approvedInvoice,
        },
        {
            reactQueryKeys: QUERY_KEYS.INVOICES,
            errorMessage: t('approve-invoice-failed', {
                invoice_id: '',
            }),
            showSuccessToast: false,
        }
    )

    const approveAll = async (method?: string) => {
        await Promise.allSettled(
            selectedInvoices?.map((id) =>
                approveMutation.mutateAsync({
                    invoiceId: Number(id),
                    approvedMethod: method,
                })
            )
        )
        setSelectedInvoices([])
    }

    // Filter out invoice_exporter_code column (only shown in Invoicable tab)
    const columns = useMemo(
        () =>
            getInvoiceListColumns(t).filter(
                // Chưa xác nhận thì chưa có chứng từ ký số
                (col) =>
                    col.key !== 'invoice_exporter_code' &&
                    !SIGNED_DOCUMENT_STATUS_FIELDS.includes(col.key)
            ),
        [t]
    )
    // Exclude invoice_status filter since this tab has fixed status
    const filterItems = useMemo(
        () =>
            getInvoiceListFilterItems(t, setFilter).filter(
                (item) =>
                    item.name !== 'invoice_status' &&
                    !SIGNED_DOCUMENT_STATUS_FIELDS.includes(item.name)
            ),
        [t, setFilter]
    )

    // Preview columns for selection dialog
    const previewColumns = useMemo(
        () => [
            { key: 'invoice_id', label: t('invoice_id') },
            {
                key: 'customer',
                label: t('customer_name'),
                render: (value: GetInvoicesRow['customer']) =>
                    value?.company_name || value?.customer_name || '---',
            },
            {
                key: 'invoice_total',
                label: t('invoice_total'),
                render: (value: number) =>
                    new Intl.NumberFormat('vi-VN').format(value || 0),
            },
        ],
        [t]
    )

    // Selection actions for toolbar
    const selectionActions = useMemo(
        () => [
            {
                key: 'non-invoicable',
                label: t('non-invoicable'),
                variant: 'warning' as const,
                onClick: () =>
                    setConfirmation({
                        open: true,
                        title: t('approved-invoice-with-non-invoicable'),
                        description: t(
                            'approved-invoice-with-non-invoicable-description',
                            {
                                invoice_ids: selectedInvoices?.join(', '),
                            }
                        ),
                        onConfirm: () => approveAll(),
                    }),
            },
            {
                key: 'invoicable',
                label: t('invoicable'),
                variant: 'default' as const,
                onClick: () =>
                    setConfirmation({
                        open: true,
                        title: t('approved-invoice-with-invoicable'),
                        description: t(
                            'approved-invoice-with-invoicable-description',
                            {
                                invoice_ids: selectedInvoices?.join(', '),
                            }
                        ),
                        onConfirm: () => approveAll('invoicable'),
                    }),
            },
        ],
        [selectedInvoices, setConfirmation, t]
    )

    const handleSelectionChange = useCallback((keys: string[]) => {
        setSelectedInvoices(keys)
    }, [])

    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )

    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )

    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    return {
        invoices: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),
        isFetching,
        params,
        columns,
        selectedInvoices,
        selectionActions,
        previewColumns,
        handleSelectionChange,
        // Filter support
        filters,
        control,
        setFilter,
        filterItems,
        searchInputProps,
        // Handlers
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        t,
    }
}
